import { Injectable, inject } from "@angular/core";
import { Overlay, OverlayPositionBuilder } from "@angular/cdk/overlay";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { TourCdk } from "../tour-cdk";
import { TourConfig } from "../models/tour-config.model";
import { TOUR_BUTTON_CLASSES } from "../tour-styles";

@Injectable({
  providedIn: "root",
})
export class TourStoreLinkService {
  private readonly overlay = inject(Overlay);
  private readonly positionBuilder = inject(OverlayPositionBuilder);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);

  private currentTour: TourCdk | null = null;
  createTour(config: TourConfig): TourCdk {
    return new TourCdk(config, this.overlay, this.positionBuilder);
  }

  startTour(config: TourConfig): void {
    this.stop();
    this.currentTour = this.createTour(config);
    this.currentTour.start();
  }

  start(): void {
    if (this.currentTour?.isRunning()) {
      return;
    }

    this.startTour(this.buildLinkStoreTourConfig());
  }

  stop(): void {
    this.currentTour?.destroy();
    this.currentTour = null;
  }

  isRunning(): boolean {
    return this.currentTour?.isRunning() ?? false;
  }

  private buildLinkStoreTourConfig(): TourConfig {
    const step4Buttons = [
      {
        text: this.translate.instant("tours.linkStore.step4.buttonDone"),
        action: "custom" as const,
        classes: TOUR_BUTTON_CLASSES.primary,
        handler: () => {
          const hasPending = !!this.findElement("[data-tour=pending-invitations-section]");
          if (hasPending) {
            this.currentTour?.next();
          } else {
            this.stop();
          }
        },
      },
    ];

    const step5 = {
      id: "step-5-pending-invitations",
      title: this.translate.instant("tours.linkStore.step5.title"),
      text: this.translate.instant("tours.linkStore.step5.text"),
      width: 400,
      element: "[data-tour=pending-invitation-accept]",
      position: "top" as const,
      beforeShow: async () => {
        const isGridMode = !!this.findElement("[data-tour=pending-invitations-grid]");
        step5.element = isGridMode
          ? "[data-tour=pending-invitations-grid] .action-button"
          : "[data-tour=pending-invitation-accept]";
        await this.waitForElement(step5.element, 5000);
      },
      allowSkip: true,
      skipText: this.translate.instant("tours.linkStore.skip"),
      buttons: [
        {
          text: this.translate.instant("tours.linkStore.step5.buttonDone"),
          action: "custom" as const,
          classes: TOUR_BUTTON_CLASSES.primary,
          handler: () => {
            this.stop();
          },
        },
      ],
    };

    return {
      id: "tour-link-store-cdk",
      name: this.translate.instant("tours.linkStore.name"),
      description: this.translate.instant("tours.linkStore.description"),
      showProgress: true,
      onSkip: () => this.stop(),
      steps: [
        {
          id: "step-1-trigger",
          title: this.translate.instant("tours.linkStore.step1.title"),
          text: this.translate.instant("tours.linkStore.step1.text"),
          element: "[data-tour='nav-management']",
          position: "right",
          beforeShow: async () => {
            const mgmtLink = this.findElement("[data-tour='nav-management']");
            if (mgmtLink && !mgmtLink.classList.contains("expanded")) {
              mgmtLink.click();
              await this.waitForCondition(
                () => mgmtLink.classList.contains("expanded"),
                1000,
                50,
              );
            }
          },
          allowSkip: true,
          skipText: this.translate.instant("tours.linkStore.skip"),
          buttons: [
            {
              text: this.translate.instant("tours.linkStore.step1.buttonNext"),
              action: "custom",
              classes: TOUR_BUTTON_CLASSES.primary,
              handler: () => {
                this.currentTour?.next();
              },
            },
          ],
        },
        {
          id: "step-2-stores",
          title: this.translate.instant("tours.linkStore.step2.title"),
          text: this.translate.instant("tours.linkStore.step2.text"),
          element: "[data-tour='nav-stores']",
          position: "right",
          allowSkip: true,
          skipText: this.translate.instant("tours.linkStore.skip"),
          buttons: [
            {
              text: this.translate.instant(
                "tours.linkStore.step2.buttonGoToStores",
              ),
              action: "custom",
              classes: TOUR_BUTTON_CLASSES.primary,
              handler: async () => {
                await this.router.navigate(["/store/stores"]);
                this.currentTour?.next();
              },
            },
          ],
        },
        {
          id: "step-3-stores-view",
          title: this.translate.instant("tours.linkStore.step3.title"),
          text: this.translate.instant("tours.linkStore.step3.text"),
          element: "[data-tour='nav-stores']",
          position: "right",
          beforeShow: async () => {
            await this.waitForElement("[data-tour=add-store-btn]", 8000);
          },
          allowSkip: true,
          skipText: this.translate.instant("tours.linkStore.skip"),
          buttons: [
            {
              text: this.translate.instant("tours.linkStore.step3.buttonNext"),
              action: "custom",
              classes: TOUR_BUTTON_CLASSES.primary,
              handler: () => {
                this.currentTour?.next();
              },
            },
          ],
        },
        {
          id: "step-4-add-store",
          title: this.translate.instant("tours.linkStore.step4.title"),
          text: this.translate.instant("tours.linkStore.step4.text"),
          width: 400,
          element: "[data-tour=add-store-btn]",
          position: "top",
          beforeShow: async () => {
            await this.waitForElement("[data-tour=add-store-btn]", 8000);
            const hasPending =
              !!this.findElement("[data-tour=pending-invitation-accept]") ||
              !!this.findElement("[data-tour=pending-invitations-grid]");
            step4Buttons[0].text = hasPending
              ? this.translate.instant("tours.linkStore.step4.buttonNext")
              : this.translate.instant("tours.linkStore.step4.buttonDone");
          },
          allowSkip: true,
          skipText: this.translate.instant("tours.linkStore.skip"),
          buttons: step4Buttons,
        },
        step5,
      ],
    };
  }

  private findElement(selector: string): HTMLElement | null {
    if (selector.startsWith("by-text:")) {
      const searchText = selector.replace("by-text:", "");
      return this.findElementByText(searchText);
    }

    return document.querySelector(selector) as HTMLElement | null;
  }

  private async waitForElement(
    selector: string,
    timeoutMs = 3000,
    intervalMs = 100,
  ): Promise<HTMLElement | null> {
    const start = performance.now();

    while (performance.now() - start < timeoutMs) {
      const element = this.findElement(selector);
      if (
        element &&
        (element.offsetParent !== null || element.getClientRects().length > 0)
      ) {
        return element;
      }
      await this.delay(intervalMs);
    }

    return null;
  }

  private findElementByText(text: string): HTMLElement | null {
    const elements = Array.from(document.querySelectorAll(".menu-list-item"));
    return elements.find((el) =>
      el.textContent?.includes(text),
    ) as HTMLElement | null;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private waitForCondition(
    condition: () => boolean,
    timeoutMs: number,
    intervalMs: number,
  ): Promise<boolean> {
    const start = performance.now();
    return new Promise((resolve) => {
      const check = () => {
        if (condition()) {
          resolve(true);
          return;
        }

        if (performance.now() - start >= timeoutMs) {
          resolve(false);
          return;
        }

        setTimeout(check, intervalMs);
      };

      check();
    });
  }
}
